import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';

// Importieren der erstellten Komponenten und Hilfsfunktionen
import EmployeeFormComponent from '../components/employee/EmployeeFormComponent';
import FileUploadComponent from '../components/employee/FileUploadComponent';
import { 
  isValidExcelFile, 
  processAnnualPlanData, 
  processHolidayData, 
  formatErrorMessage 
} from '../utils/employeeHelpers';

/**
 * @typedef {Object} EmployeePageProps
 * @property {Function} setLoggedIn - Funktion zum Setzen des Login-Status
 */

/**
 * Employee-Seite für die Verwaltung von Mitarbeiterdaten und das Hochladen von Excel-Dateien
 * 
 * @param {EmployeePageProps} props - Die Eigenschaften der Komponente
 * @returns {JSX.Element} Die Employee-Seite
 */
function Employee({ setLoggedIn }) {
    const navigate = useNavigate();
    
    // State-Variablen
    const [employee, setEmployee] = useState({});
    const [annualPlanData, setAnnualPlanData] = useState(null);
    const [holidayData, setHolidayData] = useState(null);
    const [annualPlanError, setAnnualPlanError] = useState(null);
    const [holidayPlanError, setHolidayPlanError] = useState(null);
    const [formError, setFormError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [annualPlanUploaded, setAnnualPlanUploaded] = useState(false);
    const [holidayPlanUploaded, setHolidayPlanUploaded] = useState(false);
    
    const token = localStorage.getItem('accessToken');

    /**
     * Handling für das Hochladen von Jahresplan-Dateien
     * 
     * @param {File[]} acceptedFiles - Die hochgeladenen Dateien
     */
    const handleAnnualPlanDrop = (acceptedFiles) => {
      try {
        const file = acceptedFiles[0];
        
        if (!isValidExcelFile(file)) {
          setAnnualPlanError('Ungültiges Dateiformat. Bitte laden Sie eine Excel-Datei (.xlsx oder .xls) hoch.');
          return;
        }
        
        const reader = new FileReader();
        reader.onload = function (e) {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const sheetName = workbook.SheetNames[0];
            const sheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            
            // Entfernen der Kopfzeile
            jsonData.shift();
            
            setAnnualPlanData(jsonData);
            setAnnualPlanError(null);
          } catch (error) {
            console.error('Fehler beim Lesen der Excel-Datei:', error);
            setAnnualPlanError('Die Excel-Datei konnte nicht gelesen werden. Bitte überprüfen Sie das Format.');
          }
        };
        
        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error('Fehler beim Verarbeiten der Datei:', error);
        setAnnualPlanError('Ein unerwarteter Fehler ist aufgetreten.');
      }
    };

    /**
     * Handling für das Hochladen von Urlaubsplan-Dateien
     * 
     * @param {File[]} acceptedFiles - Die hochgeladenen Dateien
     */
    const handleHolidayPlanDrop = (acceptedFiles) => {
      try {
        const file = acceptedFiles[0];
        
        if (!isValidExcelFile(file)) {
          setHolidayPlanError('Ungültiges Dateiformat. Bitte laden Sie eine Excel-Datei (.xlsx oder .xls) hoch.');
          return;
        }
        
        setHolidayPlanUploaded(false);
        const reader = new FileReader();
        
        reader.onload = (event) => {
          try {
            let workbook = null;
            workbook = XLSX.read(event.target.result, {
              cellStyles: true,
            });
            
            const sheet_name = workbook.SheetNames;
            const sheet = workbook.Sheets[sheet_name[0]];
            const cells = Object.entries(sheet).filter(([cell]) => {
              if (cell.startsWith("!")) {
                return false;
              }

              const rowNum = cell.replace(/[A-Z]+/, "");
              return rowNum > 4;
            });

            const rows = {};

            for (const cell of cells) {
              const cellId = cell[0];
              const rowNum = cellId.replace(/[A-Z]+/, "");

              if (!rows[rowNum]) {
                rows[rowNum] = [];
              }

              let status = null;

              if (cell[1].s?.patternType === "lightGrid") {
                status = "Freizeitausgleich";
              } else if (
                cell[1].s?.fgColor &&
                cell[1].s.fgColor.rgb === "DBDBDB"
              ) {
                status = "Schulung";
              } else if (
                cell[1].s?.fgColor &&
                cell[1].s.fgColor.rgb === "FF0000"
              ) {
                status = "Feiertage";
              } else if (
                (cell[1].s?.fgColor && cell[1].s.fgColor.rgb === "FFFF00") ||
                (cell[1].s?.fgColor && cell[1].s.fgColor.rgb === "FFC000")
              ) {
                status = "Ferien";
              } else if (
                cell[1].s?.fgColor &&
                cell[1].s.fgColor.rgb === "9DC3E6"
              ) {
                status = "Planung";
              } else if (
                cell[1].s?.fgColor &&
                cell[1].s.fgColor.rgb === "92D050"
              ) {
                status = "Urlaub";
              } else if (
                cell[1].s?.fgColor &&
                cell[1].s.fgColor.rgb === "AFABAB"
              ) {
                status = "Elternzeit";
              } else if (
                (cell[1].s?.fgColor &&
                  cell[1].s.fgColor.tint === -0.249977111117893) ||
                (cell[1].s?.fgColor && cell[1].s.fgColor.rgb === "BFBFBF")
              ) {
                status = "Wochenende";
              } else if (
                cell[1].s?.fgColor &&
                cell[1].s.fgColor.rgb === "7030A0"
              ) {
                status = "Bildungsurlaub";
              } else if (
                cell[1].s?.fgColor &&
                cell[1].s.fgColor.rgb === "F4B183"
              ) {
                status = "Fortbildung";
              } else if (
                cell[1].s?.fgColor &&
                cell[1].s.fgColor.rgb === "C5E0B4"
              ) {
                status = "Prüfung";
              } else if (
                cell[1].s?.fgColor &&
                cell[1].s.fgColor.rgb === "FFD966"
              ) {
                status = "load";
              } else if (cell[1].v === "K") {
                status = "Kur";
              } else if (cell[1].v === "k") {
                status = "krank";
              } else if (cell[1].v === "R") {
                status = "Reinigung";
              } else if (cell[1].v === "X") {
                status = "Kindkrank";
              }

              rows[rowNum].push({
                cellId: cell[0],
                status: status,
                value: cell[1].v,
              });
            }

            const employees = getEmployees(rows);
            setHolidayData(employees);
            setHolidayPlanError(null);

            // Hochladen der Urlaubspläne an die API
            uploadHolidayPlans(employees);
          } catch (error) {
            console.error('Fehler beim Verarbeiten der Urlaubsplandatei:', error);
            setHolidayPlanError('Die Datei konnte nicht verarbeitet werden. Bitte überprüfen Sie das Format.');
          }
        };
        
        reader.readAsArrayBuffer(file);
      } catch (error) {
        console.error('Fehler beim Verarbeiten der Datei:', error);
        setHolidayPlanError('Ein unerwarteter Fehler ist aufgetreten.');
      }
    };

    /**
     * Hochladen der Urlaubspläne an die API
     * 
     * @param {Object} holidayPlans - Die Urlaubspläne
     */
    const uploadHolidayPlans = async (holidayPlans) => {
      try {
        const response = await fetch("https://eu-central-1.aws.data.mongodb-api.com/app/data-ywwpoom/endpoint/addHolidayPlan", {
          method: "POST",
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            holidayPlans,
          }),
        });
        
        const result = await response.json();
        
        if (result.error) {
          setHolidayPlanError(`Fehler beim Speichern: ${result.error}`);
          return;
        }
        
        setHolidayPlanUploaded(true);
      } catch (error) {
        console.error('Fehler beim Speichern der Urlaubspläne:', error);
        setHolidayPlanError('Die Urlaubspläne konnten nicht gespeichert werden.');
      }
    };

    /**
     * Konvertiert die Zeilendaten in ein für die API geeignetes Format
     * 
     * @param {Object} rows - Die Zeilendaten aus der Excel-Datei
     * @returns {Object} Die konvertierten Urlaubspläne
     */
    const getEmployees = (rows) => {
      let date = new Date(`${new Date().getFullYear()}-01-01`);
      let res = {};

      for (const [, value] of Object.entries(rows)) {
        if (value[2]?.value !== undefined) {
          res[value[2].value] = value.slice(6)
            .map((obj) => {
              let newDate = new Date(date);
              date.setDate(date.getDate() + 1);
              return { ...obj, date: newDate };
            })
            .filter(timeslot => timeslot.status !== null);
        }
      }

      return res;
    };

    /**
     * Speichert die Jahrespläne
     */
    const saveAnnualPlans = async () => {
      try {
        if (!annualPlanData || annualPlanData.length === 0) {
          setAnnualPlanError('Keine Daten zum Speichern vorhanden.');
          return;
        }

        const url = process.env.REACT_APP_ANNUALPLAN_URL;
        const data = annualPlanData?.map((row, i) => {
          return {
            employeeId: row[0],
            name: row[1],
            monday: row[2],
            tuesday: row[3],
            wednesday: row[4],
            thursday: row[5],
            friday: row[6],
          };
        }).filter(plan => plan !== null && plan.employeeId);

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            annualPlans: data,
            token: token
          })
        });

        const result = await response.json();
        
        if (result.error) {
          setAnnualPlanError(`Fehler beim Speichern: ${result.error}`);
          return;
        }
        
        setAnnualPlanUploaded(true);
      } catch (error) {
        console.error('Fehler beim Speichern der Jahrespläne:', error);
        setAnnualPlanError('Die Jahrespläne konnten nicht gespeichert werden.');
      }
    };

    /**
     * Validiert den Token und lädt die Mitarbeiterdaten
     */
    const validateTokenAndLoadEmployee = async (token) => {
      if (!token) {
        setLoggedIn(false);
        navigate('/login');
        return false;
      }

      try {
        setLoading(true);
        const requestOptions = {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Access-Control-Request-Headers': '*',
          },
          body: JSON.stringify({ token }),
          redirect: 'follow'
        };

        const response = await fetch(process.env.REACT_APP_EMPLOYEE_URL, requestOptions);
        const result = await response.json();

        if (result.error) {
          setLoggedIn(false);
          navigate('/login');
          return false;
        }

        setEmployee(result);
        localStorage.setItem('company', result?.company || '');
        setLoading(false);
        return true;
      } catch (error) {
        console.error('Fehler bei der Token-Validierung:', error);
        setLoggedIn(false);
        navigate('/login');
        return false;
      }
    };

    /**
     * Behandelt Änderungen am Mitarbeiter-Formular
     * 
     * @param {Event} e - Das Event-Objekt
     */
    const handleEmployeeInputChange = (e) => {
      const { name, value } = e.target;
      setEmployee(prev => ({
        ...prev,
        [name]: value
      }));
    };

    /**
     * Speichert die Mitarbeiterdaten
     * 
     * @param {Event} e - Das Event-Objekt
     */
    const handleEmployeeFormSubmit = async (e) => {
      e.preventDefault();
      
      try {
        setIsSaving(true);
        setFormError(null);
        
        // Hier würden Sie die API-Anfrage zum Speichern der Mitarbeiterdaten implementieren
        // Beispiel:
        /*
        const response = await fetch('API_URL', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            employee
          }),
        });
        
        const result = await response.json();
        
        if (result.error) {
          setFormError(`Fehler beim Speichern: ${result.error}`);
          return;
        }
        */
        
        // Für diese Demo setzen wir einfach einen Timeout
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        setIsSaving(false);
      } catch (error) {
        console.error('Fehler beim Speichern der Mitarbeiterdaten:', error);
        setFormError('Die Mitarbeiterdaten konnten nicht gespeichert werden.');
        setIsSaving(false);
      }
    };

    // Token-Validierung und Laden der Mitarbeiterdaten beim ersten Rendern
    useEffect(() => {
      validateTokenAndLoadEmployee(token);
    }, [token, navigate, setLoggedIn]);

    // Speichern der Firma in localStorage, wenn sich der Mitarbeiter ändert
    useEffect(() => {
      if (employee?.company) {
        localStorage.setItem('company', employee.company);
      }
    }, [employee]);

    // Speichern der Jahrespläne, wenn sich die Daten ändern
    useEffect(() => {
      if (annualPlanData) {
        saveAnnualPlans();
      }
    }, [annualPlanData]);
    
    // Hauptkomponente rendern
    return (
      <div className="container mx-auto px-4 py-6">
        <h1 className="text-2xl font-bold mb-8">Mitarbeiterverwaltung</h1>
        
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-primary"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Mitarbeiter-Formular */}
            <EmployeeFormComponent 
              employee={employee}
              onInputChange={handleEmployeeInputChange}
              onSubmit={handleEmployeeFormSubmit}
              loading={isSaving}
            />
            
            {formError && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                {formError}
              </div>
            )}
            
            {/* Jahresplan-Upload */}
            <FileUploadComponent 
              onDrop={handleAnnualPlanDrop}
              title="Jahresplan hochladen"
              description="Ziehen Sie eine Excel-Datei hierher oder klicken Sie, um eine Datei auszuwählen"
              acceptedFileTypes=".xlsx, .xls"
              error={annualPlanError}
              loading={loading}
              fileUploaded={annualPlanUploaded}
            />
            
            {/* Urlaubsplan-Upload */}
            <FileUploadComponent 
              onDrop={handleHolidayPlanDrop}
              title="Urlaubsplan hochladen"
              description="Ziehen Sie eine Excel-Datei hierher oder klicken Sie, um eine Datei auszuwählen"
              acceptedFileTypes=".xlsx, .xls"
              error={holidayPlanError}
              loading={loading}
              fileUploaded={holidayPlanUploaded}
            />
            
            {/* Datenpreviews */}
            {annualPlanData && !annualPlanUploaded && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold mb-4">Vorschau: Jahresplan</h2>
                <div className="overflow-x-auto">
                  <table className="min-w-full bg-white">
                    <thead>
                      <tr>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left">ID</th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left">Name</th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left">Montag</th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left">Dienstag</th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left">Mittwoch</th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left">Donnerstag</th>
                        <th className="py-2 px-4 border-b border-gray-200 bg-gray-50 text-left">Freitag</th>
                      </tr>
                    </thead>
                    <tbody>
                      {annualPlanData.map((row, index) => (
                        <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="py-2 px-4 border-b border-gray-200">{cell}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            
            {holidayData && !holidayPlanUploaded && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-bold mb-4">Vorschau: Urlaubsplan</h2>
                <div className="overflow-x-auto max-h-96">
                  <pre className="text-xs">{JSON.stringify(holidayData, null, 2)}</pre>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
}

export default Employee;